/**
 * Report card PDF generator
 * =========================
 * Draws one A4 portrait report card per student with jsPDF and downloads
 * a real .pdf file (no print window). Header pulls school name / address /
 * phone / email / logo from the DB (same source as TC/SLC documents).
 */

import { jsPDF } from 'jspdf';
import { gradeForPercentage } from './resultsApi';

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 7;
const PAD = 4; // gap between frame and content
const INNER_L = MARGIN + PAD;
const INNER_R = PAGE_W - MARGIN - PAD;
const INNER_W = INNER_R - INNER_L;

// saturated gray palette (dark → light)
const INK = [33, 37, 41];        // primary text
const CHARCOAL = [52, 58, 64];   // header bands / frame
const STEEL = [90, 98, 106];     // secondary bands / labels
const GRAY_LINE = [173, 181, 189]; // cell borders
const MIST = [222, 226, 230];    // light borders / fills
const FOG = [243, 244, 246];     // zebra rows / box fills
const DARK = INK;
const MUTED = [120, 128, 136];
const GRAY_FILL = MIST;
const GREEN = [28, 124, 49];
const RED = [176, 33, 33];

const round = (n, d = 1) => {
  const p = 10 ** d;
  return Math.round((Number(n) || 0) * p) / p;
};

const stripBr = (v) => String(v || '').replace(/<br\s*\/?>/gi, ', ').replace(/\s+/g, ' ').trim();

/** Load the logo as a data URL; resolves null on any failure. */
const loadImage = (url) => new Promise((resolve) => {
  if (!url) { resolve(null); return; }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  const timer = setTimeout(() => resolve(null), 4000);
  img.onload = () => {
    clearTimeout(timer);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({ data: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
    } catch {
      resolve(null); // canvas tainted (CORS)
    }
  };
  img.onerror = () => { clearTimeout(timer); resolve(null); };
  img.src = url;
});

const loadLogo = async (url) => {
  const img = await loadImage(url);
  return img ? img.data : null;
};

/* ------------------------------------------------------------------ */
/* Drawing helpers                                                     */
/* ------------------------------------------------------------------ */

const drawFrame = (doc) => {
  // single thin frame
  doc.setDrawColor(...CHARCOAL);
  doc.setLineWidth(0.4);
  doc.rect(MARGIN, MARGIN, PAGE_W - MARGIN * 2, PAGE_H - MARGIN * 2);
};

const drawHeader = (doc, school, logoData) => {
  let y = MARGIN + 11;

  if (logoData) {
    try { doc.addImage(logoData, 'PNG', INNER_L, y - 5, 20, 20); } catch { /* skip bad image */ }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...DARK);
  doc.text(school?.schoolName || 'School Name', PAGE_W / 2, y + 1, { align: 'center' });
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  const addr = stripBr(school?.schoolAddress);
  if (addr) {
    const lines = doc.splitTextToSize(addr, INNER_W - 52);
    doc.text(lines, PAGE_W / 2, y, { align: 'center' });
    y += lines.length * 3.8;
  }
  if (school?.schoolPhone) {
    doc.text(`Ph: ${school.schoolPhone}`, PAGE_W / 2, y, { align: 'center' });
    y += 3.8;
  }
  if (school?.schoolEmail) {
    doc.text(String(school.schoolEmail), PAGE_W / 2, y, { align: 'center' });
    y += 3.8;
  }

  y += 1.5;
  doc.setDrawColor(...CHARCOAL);
  doc.setLineWidth(0.4);
  doc.line(INNER_L, y, INNER_R, y);
  return y + 3;
};

const drawBanner = (doc, y, examName) => {
  doc.setFillColor(...CHARCOAL);
  doc.setDrawColor(...CHARCOAL);
  doc.setLineWidth(0.2);
  doc.rect(INNER_L, y, INNER_W, 8.5, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  doc.text('PROGRESS REPORT CARD', INNER_L + 3, y + 5.7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...MIST);
  doc.text(String(examName || ''), INNER_R - 3, y + 5.7, { align: 'right' });
  return y + 12;
};

const infoCell = (doc, x, y, w, h, label, value) => {
  doc.setDrawColor(...MIST);
  doc.setLineWidth(0.2);
  doc.setFillColor(...FOG);
  doc.rect(x, y, w, h, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.3);
  doc.setTextColor(...STEEL);
  doc.text(label.toUpperCase(), x + 2.5, y + 3.8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  // missing details stay blank
  doc.text(String(value || ''), x + 2.5, y + 8.8);
};

const drawStudentInfo = (doc, y, student) => {
  const photoW = 25;
  const gap = 3;
  const gridW = INNER_W - photoW - gap;
  const h = 10.5;
  const w = gridW / 2;
  const boxH = h * 3;

  infoCell(doc, INNER_L, y, w, h, 'Student Name', student.Student_Name);
  infoCell(doc, INNER_L + w, y, w, h, 'Admission No.', student.Admission_Number);
  infoCell(doc, INNER_L, y + h, w, h, 'Class — Section',
    `${student.Class || ''}${student.Section ? ` — ${student.Section}` : ''}`);
  infoCell(doc, INNER_L + w, y + h, w, h, 'Student ID', student.Student_Id);
  infoCell(doc, INNER_L, y + h * 2, w, h, "Father's Name", student.Father_Name);
  infoCell(doc, INNER_L + w, y + h * 2, w, h, "Mother's Name", student.Mother_Name);

  // photo placeholder box
  const px = INNER_L + gridW + gap;
  doc.setDrawColor(...GRAY_LINE);
  doc.setLineWidth(0.2);
  doc.setFillColor(...FOG);
  doc.rect(px, y, photoW, boxH, 'FD');
  doc.setLineDashPattern([1.2, 1.2], 0);
  doc.rect(px + 1.5, y + 1.5, photoW - 3, boxH - 3);
  doc.setLineDashPattern([], 0);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text('PHOTO', px + photoW / 2, y + boxH / 2 + 1, { align: 'center' });

  // divider before the marks table
  const dy = y + boxH + 3.5;
  doc.setDrawColor(...GRAY_LINE);
  doc.setLineWidth(0.3);
  doc.line(INNER_L, dy, INNER_R, dy);
  return dy + 3.5;
};

const drawMarksTable = (doc, y, student, subjects, grades) => {
  const subs = subjects.filter((sub) => student.marks?.[sub]);
  // column widths: SN, Subject, Obtained, Full, %, Grade
  const cw = [11, INNER_W - 11 - 26 - 24 - 24 - 20, 26, 24, 24, 20];
  const cx = [INNER_L];
  cw.forEach((w, i) => cx.push(cx[i] + w));

  const headH = 8;
  // shrink rows if many subjects so everything stays on one page
  const rowH = subs.length > 12 ? 6.2 : 7.5;
  // grades left-align from a fixed point near the column centre so A / A+ line up
  const gradeX = cx[5] + cw[5] / 2 - 2.5;

  doc.setFillColor(...CHARCOAL);
  doc.setDrawColor(...CHARCOAL);
  doc.setLineWidth(0.2);
  doc.rect(INNER_L, y, INNER_W, headH, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  const heads = ['SN', 'SUBJECT', 'MARKS OBTAINED', 'FULL MARKS', 'PERCENTAGE', 'GRADE'];
  heads.forEach((hTxt, i) => {
    const center = cx[i] + cw[i] / 2;
    if (i === 1) doc.text(hTxt, cx[i] + 3, y + 5.3);
    else doc.text(hTxt, center, y + 5.3, { align: 'center' });
  });
  doc.setDrawColor(...STEEL);
  for (let i = 1; i < cx.length - 1; i++) doc.line(cx[i], y, cx[i], y + headH);
  y += headH;

  doc.setFontSize(9);
  subs.forEach((sub, idx) => {
    const m = student.marks[sub];
    const pct = m.full ? round((m.obtained / m.full) * 100) : 0;
    const grade = m.obtained === 0 ? 'AB' : gradeForPercentage(pct, grades);

    if (idx % 2 === 1) {
      doc.setFillColor(...FOG);
      doc.rect(INNER_L, y, INNER_W, rowH, 'F');
    }
    doc.setDrawColor(...MIST);
    doc.setLineWidth(0.2);
    doc.rect(INNER_L, y, INNER_W, rowH);
    for (let i = 1; i < cx.length - 1; i++) doc.line(cx[i], y, cx[i], y + rowH);

    const ty = y + rowH / 2 + 1.2;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...DARK);
    doc.text(String(idx + 1), cx[0] + cw[0] / 2, ty, { align: 'center' });
    doc.text(String(sub), cx[1] + 3, ty);
    doc.text(String(m.obtained), cx[2] + cw[2] / 2, ty, { align: 'center' });
    doc.text(String(m.full), cx[3] + cw[3] / 2, ty, { align: 'center' });
    doc.text(`${pct}%`, cx[4] + cw[4] / 2, ty, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.text(String(grade), gradeX, ty);
    y += rowH;
  });

  // TOTAL row
  const pct = round(student.percentage || 0);
  const overall = student.obtained === 0 ? 'AB' : gradeForPercentage(pct, grades);
  doc.setFillColor(...STEEL);
  doc.setDrawColor(...STEEL);
  doc.rect(INNER_L, y, INNER_W, headH, 'FD');
  doc.setDrawColor(...GRAY_LINE);
  for (let i = 2; i < cx.length - 1; i++) doc.line(cx[i], y, cx[i], y + headH);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(255, 255, 255);
  const ty = y + 5.4;
  doc.text('TOTAL', cx[0] + 3, ty);
  doc.text(String(student.obtained), cx[2] + cw[2] / 2, ty, { align: 'center' });
  doc.text(String(student.fullMarks), cx[3] + cw[3] / 2, ty, { align: 'center' });
  doc.text(`${pct}%`, cx[4] + cw[4] / 2, ty, { align: 'center' });
  doc.text(String(overall), gradeX, ty);

  return { y: y + headH + 6, pct, overall };
};

const drawSummary = (doc, y, student, pct, overall) => {
  const result = student.obtained === 0 ? 'ABSENT' : (pct >= 33 ? 'PASS' : 'FAIL');
  const boxes = [
    ['Overall Marks', `${student.obtained} / ${student.fullMarks}`, DARK],
    ['Overall Percentage', `${pct}%`, DARK],
    ['Overall Grade', String(overall), DARK],
    ['Result', result, result === 'PASS' ? GREEN : RED],
  ];
  const gap = 3;
  const w = (INNER_W - gap * 3) / 4;
  const h = 14;
  boxes.forEach(([label, value, color], i) => {
    const x = INNER_L + i * (w + gap);
    doc.setDrawColor(...MIST);
    doc.setLineWidth(0.2);
    doc.setFillColor(...FOG);
    doc.rect(x, y, w, h, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...STEEL);
    doc.text(label.toUpperCase(), x + w / 2, y + 4.5, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...color);
    doc.text(value, x + w / 2, y + 10.8, { align: 'center' });
  });
  return y + h + 6;
};

const drawGradeScale = (doc, y, grades) => {
  if (!grades.length) return y;
  const labelW = 26;
  const cellW = (INNER_W - labelW) / grades.length;
  const h = 9;

  doc.setDrawColor(...MIST);
  doc.setLineWidth(0.2);
  doc.setFillColor(...CHARCOAL);
  doc.rect(INNER_L, y, labelW, h, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(255, 255, 255);
  doc.text('GRADE SCALE', INNER_L + labelW / 2, y + 5.3, { align: 'center' });

  grades.forEach((g, i) => {
    const x = INNER_L + labelW + i * cellW;
    doc.setFillColor(...(i % 2 === 1 ? FOG : [255, 255, 255]));
    doc.rect(x, y, cellW, h, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...DARK);
    doc.text(String(g.grade), x + cellW / 2, y + 3.8, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...MUTED);
    doc.text(`${g.min}–${g.max}%`, x + cellW / 2, y + 7.3, { align: 'center' });
  });
  return y + h + 6;
};

/** Co-scholastic areas table — two columns of Area | Grade (blank, filled by hand). */
const drawCoScholastic = (doc, y, areas) => {
  if (!areas?.length) return y;

  const headH = 7;
  doc.setFillColor(...CHARCOAL);
  doc.setDrawColor(...CHARCOAL);
  doc.setLineWidth(0.2);
  doc.rect(INNER_L, y, INNER_W, headH, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(255, 255, 255);
  doc.text('CO-SCHOLASTIC AREAS', INNER_L + 3, y + 4.8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...MIST);
  doc.text('Graded by class teacher', INNER_R - 3, y + 4.8, { align: 'right' });
  y += headH;

  // two side-by-side Area|Grade columns
  const gradeW = 18;
  const colW = INNER_W / 2;
  const rowH = 6.5;
  const rows = Math.ceil(areas.length / 2);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < 2; c++) {
      const idx = c * rows + r;
      const x = INNER_L + c * colW;
      if (r % 2 === 1) {
        doc.setFillColor(...FOG);
        doc.rect(x, y + r * rowH, colW, rowH, 'F');
      }
      doc.setDrawColor(...MIST);
      doc.setLineWidth(0.2);
      doc.rect(x, y + r * rowH, colW - gradeW, rowH);
      doc.rect(x + colW - gradeW, y + r * rowH, gradeW, rowH);
      if (idx < areas.length) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...DARK);
        doc.text(String(areas[idx]), x + 3, y + r * rowH + rowH / 2 + 1.1);
      }
    }
  }

  return y + rows * rowH + 5;
};

/** Remarks box — remark text comes from the grade band configuration. */
const drawRemarks = (doc, y, remark) => {
  const h = 13;
  doc.setDrawColor(...MIST);
  doc.setLineWidth(0.2);
  doc.setFillColor(...FOG);
  doc.rect(INNER_L, y, INNER_W, h, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(...STEEL);
  doc.text('REMARKS', INNER_L + 3, y + 4.2);
  if (remark) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    doc.text(String(remark), INNER_L + 3, y + 9.6);
  } else {
    // blank line for a handwritten remark
    doc.setDrawColor(...GRAY_LINE);
    doc.setLineWidth(0.2);
    doc.line(INNER_L + 3, y + 9.6, INNER_R - 3, y + 9.6);
  }
  return y + h + 5;
};

/**
 * Signatures — dynamic count/position:
 * 1 → right · 2 → left + right · 3 → left + center + right · 4 → spread left to right.
 * Uploaded signature images print above the line.
 */
const drawSignatures = (doc, signatures = [], sigImages = []) => {
  const y = PAGE_H - MARGIN - 16;
  const w = 42;
  const n = signatures.length;

  if (n > 0) {
    let positions;
    if (n === 1) positions = [INNER_R - w];
    else if (n === 2) positions = [INNER_L, INNER_R - w];
    else if (n === 3) positions = [INNER_L, PAGE_W / 2 - w / 2, INNER_R - w];
    else positions = signatures.map((_, i) => INNER_L + i * ((INNER_W - w) / (n - 1)));

    doc.setFontSize(8.5);
    signatures.forEach((sig, i) => {
      const x = positions[i];
      const img = sigImages[i];
      // image box above the line, centered, max 34×11 mm
      const maxW = 34;
      const maxH = 11;
      if (img) {
        const scale = Math.min(maxW / img.w, maxH / img.h);
        const iw = img.w * scale;
        const ih = img.h * scale;
        try { doc.addImage(img.data, 'PNG', x + (w - iw) / 2, y - ih - 1, iw, ih); } catch { /* skip bad image */ }
        if (sig.border) {
          doc.setDrawColor(...GRAY_LINE);
          doc.setLineWidth(0.2);
          doc.setLineDashPattern([1, 1], 0);
          doc.rect(x + (w - iw) / 2 - 1.5, y - ih - 2.5, iw + 3, ih + 3);
          doc.setLineDashPattern([], 0);
        }
      } else if (sig.border) {
        // empty dotted box where the signature goes
        doc.setDrawColor(...GRAY_LINE);
        doc.setLineWidth(0.2);
        doc.setLineDashPattern([1, 1], 0);
        doc.rect(x + (w - maxW) / 2, y - maxH - 1, maxW, maxH);
        doc.setLineDashPattern([], 0);
      }
      doc.setDrawColor(...STEEL);
      doc.setLineWidth(0.25);
      doc.line(x, y, x + w, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...STEEL);
      doc.text(String(sig.label || ''), x + w / 2, y + 4.5, { align: 'center' });
    });
  }

  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(`Issued on ${new Date().toLocaleDateString()}`, INNER_R, PAGE_H - MARGIN - 4, { align: 'right' });
};

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/**
 * Build and download a PDF with one report card per student.
 * @param {Array|object} studentOrList one student row or an array of rows
 * @param {object} extras optional { coScholastic: [...], signatures: [{ label, url }] }
 */
export const printReportCards = async (studentOrList, exam, subjects, grades = [], school = null, extras = {}) => {
  const list = Array.isArray(studentOrList) ? studentOrList : [studentOrList];
  if (!list.length) return;

  const signatures = (extras.signatures || []).slice(0, 4);
  const [logoData, ...sigImages] = await Promise.all([
    loadLogo(school?.schoolLogo),
    ...signatures.map((s) => loadImage(s.url)),
  ]);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  list.forEach((student, i) => {
    if (i > 0) doc.addPage();
    drawFrame(doc);
    let y = drawHeader(doc, school, logoData);
    y = drawBanner(doc, y, exam?.Exam_Name);
    y = drawStudentInfo(doc, y, student);
    const { y: yAfter, pct, overall } = drawMarksTable(doc, y, student, subjects, grades);
    y = drawSummary(doc, yAfter, student, pct, overall);
    y = drawGradeScale(doc, y, grades);
    y = drawCoScholastic(doc, y, extras.coScholastic);
    const band = grades.find((g) => String(g.grade) === String(overall));
    drawRemarks(doc, y, band?.remark);
    drawSignatures(doc, signatures, sigImages);
  });

  const examName = (exam?.Exam_Name || 'Exam').replace(/[\\/:*?"<>|]/g, '');
  const fileName = list.length === 1
    ? `${(list[0].Student_Name || 'Student').replace(/[\\/:*?"<>|]/g, '')} - ${examName} Report Card.pdf`
    : `${examName} - Report Cards.pdf`;
  doc.save(fileName);
};

